/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { IconButton } from '@strata/react-buttons';
import { CollapsibleSection } from '@strata/react-collapsible-section';
import { FormSection } from '@strata/react-form-section';
import { Grid, PrimaryData, SecondaryData } from '@strata/react-grid';
import { InlineMessage } from '@strata/react-inline-message';
import { DetailLabel, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { MessagingBlock } from '@strata/react-messaging-block';
import { ClaimStatusIndicator, StatusIndicator } from '@strata/react-status-indicators';
import { Tag } from '@strata/react-tags';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { useGetPrescriberDetailsQuery, useGetSupplierDetailsQuery } from 'store/api/referenceApi';
import { useGetTestClaimsForTreatmentQuery } from 'store/api/treatmentApi';
import { Grids, useGridState } from 'store/grids/gridsSlice';

// Sorted by when the claim ran, not by date of service: "latest" means the most recent run,
// and a claim can be re-run for an older date of service.
const initialSortProperties = [{ id: 'createdAt', desc: true }];

const Dispositions = {
    PAID: 'paid',
    REJECTED: 'rejected',
};

const ConsequenceByOutcome = {
    AWAITING_AUTH: {
        intent: InlineMessage.Intent.WARNING,
        label: (rejectCode) =>
            `Reject ${rejectCode} keeps this treatment manageable — the referral was moved to Awaiting Auth rather than unmanaged.`,
    },
    UNMANAGE: {
        intent: InlineMessage.Intent.WARNING,
        label: (rejectCode) =>
            `Reject ${rejectCode} is not manageable — the treatment was unmanaged.`,
    },
    ASSIGN: {
        intent: InlineMessage.Intent.INFO,
        label: (rejectCode) =>
            `Reject ${rejectCode} keeps this treatment manageable — the claim is still assignable.`,
    },
};

/** The reject the outcome was driven by, falling back to the first coded reject for display. */
const getDisplayRejectCode = ({ rejectCode, rejectCodes }) => rejectCode ?? rejectCodes?.[0]?.code;

// Prefer the message on the reject that drove the outcome, then any coded reject, then a
// code-less message. The PBM can reject with only code-less text, or with nothing at all.
const getDisplayRejectMessage = ({ rejectCode, rejectCodes, rejectMessages }) =>
    (rejectCodes?.find(({ code }) => code === rejectCode) ?? rejectCodes?.[0])?.message ??
    rejectMessages?.[0];

const composeNpiWithName = (name, npi) => (name ? `${name} (${npi})` : npi);

// test_claim_reject_code.message is nullable, so a coded reject can arrive without one.
const formatReject = (code, message) => [code, message].filter(Boolean).join(' · ');

/** PAID and REJECTED come from the PBM's disposition; an absent disposition is not payable. */
const ResultIndicator = ({ disposition = undefined }) => {
    if (disposition === Dispositions.PAID) {
        return <ClaimStatusIndicator status={ClaimStatusIndicator.Statuses.PAID} />;
    }
    if (disposition === Dispositions.REJECTED) {
        return <ClaimStatusIndicator status={ClaimStatusIndicator.Statuses.REJECTED} />;
    }
    return (
        <StatusIndicator
            status={StatusIndicator.Status.DEFAULT}
            message="No Response"
            tooltipText="The PBM returned no disposition — treat as not payable."
        />
    );
};
ResultIndicator.propTypes = {
    disposition: PropTypes.string,
};

const RejectCodesCell = ({ row }) => {
    const { rejectCodes } = row.original;
    if (!rejectCodes?.length) {
        return <SecondaryData data="None" />;
    }
    const [firstRejectCode, ...additionalRejectCodes] = rejectCodes;
    return (
        <SecondaryData
            customTooltipContent={null}
            data={
                <>
                    <div className="font-semi-bold">
                        {formatReject(firstRejectCode.code, firstRejectCode.message)}
                    </div>
                    {additionalRejectCodes.length > 0 && (
                        <div className="sds_test-claims__caption">
                            {`+${additionalRejectCodes.length} more`}
                        </div>
                    )}
                </>
            }
        />
    );
};
RejectCodesCell.propTypes = {
    row: PropTypes.shape({
        original: PropTypes.shape({
            rejectCodes: PropTypes.arrayOf(
                PropTypes.shape({ code: PropTypes.string, message: PropTypes.string }),
            ),
        }),
    }).isRequired,
};

// Widths total 850px on purpose: .griddle-table is table-layout: fixed inside an
// overflow: hidden .sds_form-section, so a wider total silently clips the last columns.
const columns = (latestTestClaimRequestId) => [
    {
        accessorKey: 'createdAt',
        header: 'Run At',
        size: 160,
        // The tag sits under the timestamp rather than beside it: the column is only wide
        // enough for one, and a fixed-layout table clips the overflow instead of wrapping.
        cell: ({ getValue, row }) => (
            <PrimaryData
                data={
                    <>
                        <div>{formatDate(getValue(), DateFormats.DATE_TIME)}</div>
                        {row.original.testClaimRequestId === latestTestClaimRequestId && (
                            <div className="padding__top--xx-small">
                                <Tag label="Latest" />
                            </div>
                        )}
                    </>
                }
            />
        ),
    },
    {
        accessorKey: 'dateOfService',
        header: 'Date of Service',
        size: 92,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'ndc',
        header: 'NDC',
        size: 118,
        cell: ({ getValue }) => <SecondaryData data={getValue()} />,
    },
    {
        accessorKey: 'pharmacyNpi',
        header: 'Supplier NPI',
        size: 110,
        cell: ({ getValue }) => <SecondaryData data={getValue()} />,
    },
    {
        accessorKey: 'rejectCodes',
        header: 'Reject Codes',
        size: 240,
        enableSorting: false,
        cell: RejectCodesCell,
    },
    {
        accessorKey: 'disposition',
        header: 'Result',
        size: 130,
        enableSorting: false,
        // The indicator grows to fill its cell, so nothing else belongs in here.
        cell: ({ getValue }) => <ResultIndicator disposition={getValue()} />,
    },
];

const TestClaimDetail = ({ testClaim, onClose }) => {
    const { data: supplierDetails } = useGetSupplierDetailsQuery(
        testClaim.pharmacyNpi ? { supplierNpi: testClaim.pharmacyNpi } : skipToken,
    );
    const { data: prescriberDetails } = useGetPrescriberDetailsQuery(
        testClaim.prescriberNpi ? { prescriberNpi: testClaim.prescriberNpi } : skipToken,
    );

    const displayRejectCode = getDisplayRejectCode(testClaim);
    const displayRejectMessage = getDisplayRejectMessage(testClaim);
    const consequence = ConsequenceByOutcome[testClaim.outcome];
    const isRejected = testClaim.disposition === Dispositions.REJECTED;
    // The PBM can reject with a code and a message, with a message and no code, or with
    // neither - which is also what the dev mock stores when no response is configured.
    const rejectSummary = formatReject(displayRejectCode, displayRejectMessage) || 'None returned';

    return (
        <FormSection
            sectionTitle={`${formatDate(testClaim.dateOfService)} Detail`}
            rightHeaderContent={
                <IconButton icon={IconNames.CROSS} title="Close detail" onClick={onClose} />
            }
            content={
                <div className="flex__gap--large" style={{ flexDirection: 'column' }}>
                    {consequence && (
                        <InlineMessage
                            intent={consequence.intent}
                            wrapText
                            label={consequence.label(displayRejectCode)}
                        />
                    )}
                    <div className="sds_test-claims__request-grid">
                        <DetailLabelValuePair
                            label="Disposition"
                            value={<ResultIndicator disposition={testClaim.disposition} />}
                        />
                        {isRejected && (
                            <DetailLabelValuePair label="Reject Code" value={rejectSummary} />
                        )}
                        <DetailLabelValuePair
                            label="Billing Supplier"
                            value={composeNpiWithName(
                                supplierDetails?.organizationName,
                                testClaim.pharmacyNpi,
                            )}
                        />
                        <DetailLabelValuePair
                            label="Prescriber"
                            value={composeNpiWithName(
                                prescriberDetails?.prescriberName,
                                testClaim.prescriberNpi,
                            )}
                        />
                    </div>
                    {testClaim.rejectCodes?.length > 0 && (
                        <div>
                            <DetailLabel label="Reject Codes Returned" />
                            {testClaim.rejectCodes.map(({ code, message }) => (
                                <div
                                    key={`${code}-${message}`}
                                    className="sds_test-claims__reject-code flex__gap--small padding__top--x-small"
                                >
                                    <Tag
                                        label={code}
                                        intent={
                                            code === testClaim.rejectCode
                                                ? Tag.Intent.ERROR
                                                : Tag.Intent.NONE
                                        }
                                    />
                                    <span className="font-semi-bold">{message}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {testClaim.rejectMessages?.length > 0 && (
                        <div>
                            <DetailLabel label="Messages Without a Reject Code" />
                            {testClaim.rejectMessages.map((rejectMessage) => (
                                <div
                                    key={rejectMessage}
                                    className="sds_test-claims__pbm-message margin__top--x-small"
                                >
                                    {rejectMessage}
                                </div>
                            ))}
                        </div>
                    )}
                    <CollapsibleSection
                        title="Request sent to the PBM"
                        content={
                            <div className="sds_test-claims__request-grid padding__top--small">
                                <DetailLabelValuePair label="NDC" value={testClaim.ndc} />
                                <DetailLabelValuePair
                                    label="Pharmacy NPI"
                                    value={testClaim.pharmacyNpi}
                                />
                                <DetailLabelValuePair
                                    label="Prescriber NPI"
                                    value={testClaim.prescriberNpi}
                                />
                                <DetailLabelValuePair
                                    label="Date of Service"
                                    value={formatDate(testClaim.dateOfService)}
                                />
                                <DetailLabelValuePair label="Quantity" value={testClaim.quantity} />
                                <DetailLabelValuePair
                                    label="Days Supply"
                                    value={testClaim.daysSupply}
                                />
                                <DetailLabelValuePair label="DAW Code" value={testClaim.dawCode} />
                                <DetailLabelValuePair
                                    label="Member ID"
                                    value={testClaim.memberId}
                                />
                            </div>
                        }
                    />
                </div>
            }
        />
    );
};
TestClaimDetail.propTypes = {
    testClaim: PropTypes.shape({
        createdAt: PropTypes.string,
        dateOfService: PropTypes.string,
        disposition: PropTypes.string,
        outcome: PropTypes.string,
        rejectCode: PropTypes.string,
        rejectCodes: PropTypes.arrayOf(PropTypes.shape({})),
        rejectMessages: PropTypes.arrayOf(PropTypes.string),
        ndc: PropTypes.string,
        pharmacyNpi: PropTypes.string,
        prescriberNpi: PropTypes.string,
        quantity: PropTypes.number,
        daysSupply: PropTypes.number,
        dawCode: PropTypes.number,
        memberId: PropTypes.string,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
};

export const TestClaims = ({ payerId, treatmentId, segmentId = undefined }) => {
    const { sort, updateGridSort } = useGridState(Grids.TEST_CLAIMS, initialSortProperties);
    const [selectedTestClaimRequestId, setSelectedTestClaimRequestId] = useState();
    const { data: testClaims, isFetching } = useGetTestClaimsForTreatmentQuery(
        segmentId ? { payerId, treatmentId, segmentId } : skipToken,
    );

    // The endpoint returns newest first, so the latest claim is the first row whatever the
    // grid is sorted by. undefined means "not chosen yet", so the newest opens by default;
    // null means the user closed the drawer and should not have it reopened for them.
    const latestTestClaim = testClaims?.[0];
    const selectedTestClaim =
        selectedTestClaimRequestId === null
            ? undefined
            : (testClaims?.find(
                  ({ testClaimRequestId }) => testClaimRequestId === selectedTestClaimRequestId,
              ) ?? latestTestClaim);

    return (
        <div className="flex__gap--medium" style={{ alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <FormSection
                    sectionTitle="FMH Test Claim History"
                    className="sds_treatment__grid"
                    rightHeaderContent={
                        testClaims?.length > 0 && (
                            <span className="sds_test-claims__caption">
                                {`${testClaims.length} FMH test claim${testClaims.length === 1 ? '' : 's'}`}
                            </span>
                        )
                    }
                    content={
                        <Grid
                            data={testClaims}
                            columns={columns(latestTestClaim?.testClaimRequestId)}
                            isLoading={isFetching || !segmentId}
                            enableSorting
                            sortProperties={sort}
                            onSort={updateGridSort}
                            onRowClick={({ testClaimRequestId }) =>
                                setSelectedTestClaimRequestId(
                                    testClaimRequestId === selectedTestClaim?.testClaimRequestId
                                        ? null
                                        : testClaimRequestId,
                                )
                            }
                            rowCssClass={({ testClaimRequestId }) =>
                                classNames('sds_treatment__grid__row', {
                                    'sds_treatment__grid__row--active':
                                        testClaimRequestId ===
                                        selectedTestClaim?.testClaimRequestId,
                                })
                            }
                            noResults={
                                <div className="padding__around--large">
                                    <MessagingBlock
                                        size={MessagingBlock.Sizes.SMALL}
                                        icon={IconNames.PRESCRIPTION}
                                        primaryText="No FMH test claims for this treatment"
                                    />
                                </div>
                            }
                        />
                    }
                />
            </div>
            {selectedTestClaim && (
                <div style={{ flex: '0 0 420px' }}>
                    <TestClaimDetail
                        testClaim={selectedTestClaim}
                        onClose={() => setSelectedTestClaimRequestId(null)}
                    />
                </div>
            )}
        </div>
    );
};

TestClaims.propTypes = {
    payerId: PropTypes.string.isRequired,
    treatmentId: PropTypes.string.isRequired,
    segmentId: PropTypes.string,
};
